/**
 * Social Sharing Component - Viral Mechanics for Techno Community
 * 
 * Features:
 * - Share to Twitter/X, Instagram Stories, WhatsApp, Telegram
 * - Pre-formatted messages for electronic music community
 * - Booking confirmation shareable cards
 * - Event discovery sharing
 */

import React from 'react';
import { Share2, Twitter, MessageCircle, Send, Copy, Check } from 'lucide-react';
import { toast } from 'sonner';

// Viral hashtags for techno/electronic community
const VIRAL_HASHTAGS = [
  '#techno',
  '#electronicmusic', 
  '#rave',
  '#undergroundmusic',
  '#housemusic',
  '#melodictechno',
  '#berghain',
  '#ibiza',
  '#warehouse',
  '#XTrip'
];

const getRandomHashtags = (count = 5) => {
  const shuffled = [...VIRAL_HASHTAGS].sort(() => 0.5 - Math.random());
  return shuffled.slice(0, count).join(' ');
};

/**
 * Generate shareable text for an event
 */
export const generateEventShareText = (event, bundle = null) => {
  const eventName = event?.name || 'an underground event';
  const city = event?.venue_city || '';
  const genre = event?.genre || 'Electronic';
  const hashtags = getRandomHashtags(4);
  
  if (bundle) {
    return `Just booked my trip to ${eventName} in ${city} 🎫✈️🏨\n\nFlight + Hotel + Ticket sorted in one checkout.\n\n${hashtags} #XTrip`;
  }
  
  return `${eventName} in ${city} 🔊\n\n${genre} vibes only.\n\nBook your trip: xtrip.com\n\n${hashtags}`;
};

/**
 * Generate booking confirmation share text
 */
export const generateBookingShareText = (booking, event) => {
  const eventName = event?.name || 'the event';
  const city = event?.venue_city || '';
  const date = event?.start_at ? new Date(event.start_at).toLocaleDateString('en-GB', { month: 'short', day: 'numeric' }) : '';
  
  return `✅ CONFIRMED: ${eventName}\n📍 ${city} ${date}\n\nFlight, hotel, ticket - all sorted.\n\nPlan your next rave: xtrip.com\n\n${getRandomHashtags(3)} #XTrip`;
};

/**
 * Share URLs for different platforms
 */
const getShareUrl = (platform, text, url = 'https://xtrip.com') => {
  const encodedText = encodeURIComponent(text);
  const encodedUrl = encodeURIComponent(url);
  
  switch (platform) {
    case 'twitter':
      return `https://twitter.com/intent/tweet?text=${encodedText}&url=${encodedUrl}`;
    case 'whatsapp':
      return `https://wa.me/?text=${encodedText}%20${encodedUrl}`;
    case 'telegram':
      return `https://t.me/share/url?url=${encodedUrl}&text=${encodedText}`;
    case 'facebook':
      return `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}&quote=${encodedText}`;
    default:
      return null;
  }
};

/**
 * Copy to clipboard with haptic feedback
 */
const copyToClipboard = async (text) => {
  try {
    await navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard!');
    return true;
  } catch (err) {
    toast.error('Failed to copy');
    return false;
  }
};

/**
 * Share Button Component
 */
export const ShareButton = ({ 
  event, 
  bundle = null, 
  variant = 'icon', 
  className = '' 
}) => {
  const [copied, setCopied] = React.useState(false);
  const [showMenu, setShowMenu] = React.useState(false);
  
  const shareText = bundle 
    ? generateBookingShareText(bundle, event)
    : generateEventShareText(event, bundle);
  
  const shareUrl = typeof window !== 'undefined' ? window.location.href : 'https://xtrip.com';
  
  const handleShare = async (platform) => {
    if (platform === 'copy') {
      const success = await copyToClipboard(`${shareText}\n\n${shareUrl}`);
      if (success) {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      }
    } else if (platform === 'native' && navigator.share) {
      try {
        await navigator.share({
          title: event?.name || 'XTrip Event',
          text: shareText,
          url: shareUrl
        });
      } catch (err) {
        // User cancelled or error
      }
    } else {
      const url = getShareUrl(platform, shareText, shareUrl);
      if (url) {
        window.open(url, '_blank', 'width=600,height=400');
      }
    }
    setShowMenu(false);
  };
  
  if (variant === 'icon') {
    return (
      <div className="relative">
        <button
          onClick={() => setShowMenu(!showMenu)}
          className={`p-2 rounded-full glass hover:bg-primary/20 transition-colors ${className}`}
          data-testid="share-button"
          aria-label="Share"
        >
          <Share2 className="w-4 h-4 text-foreground" />
        </button>
        
        {showMenu && (
          <div 
            className="absolute right-0 top-full mt-2 glass-solid rounded-xl p-2 z-50 min-w-[180px] shadow-xl"
            style={{ fontFamily: "'Space Mono', monospace" }}
          >
            <button
              onClick={() => handleShare('twitter')}
              className="w-full flex items-center gap-3 px-3 py-2 hover:bg-foreground/10 rounded-lg text-sm"
            >
              <Twitter className="w-4 h-4 text-[#1DA1F2]" />
              <span>Twitter / X</span>
            </button>
            <button
              onClick={() => handleShare('whatsapp')}
              className="w-full flex items-center gap-3 px-3 py-2 hover:bg-foreground/10 rounded-lg text-sm"
            >
              <MessageCircle className="w-4 h-4 text-[#25D366]" />
              <span>WhatsApp</span>
            </button>
            <button
              onClick={() => handleShare('telegram')}
              className="w-full flex items-center gap-3 px-3 py-2 hover:bg-foreground/10 rounded-lg text-sm"
            >
              <Send className="w-4 h-4 text-[#0088cc]" />
              <span>Telegram</span>
            </button>
            <div className="border-t border-foreground/10 my-1" />
            <button
              onClick={() => handleShare('copy')}
              className="w-full flex items-center gap-3 px-3 py-2 hover:bg-foreground/10 rounded-lg text-sm"
            >
              {copied ? (
                <Check className="w-4 h-4 text-green-400" />
              ) : (
                <Copy className="w-4 h-4 text-muted-foreground" />
              )}
              <span>{copied ? 'Copied!' : 'Copy Link'}</span>
            </button>
          </div>
        )}
      </div>
    );
  }
  
  // Full button variant
  return (
    <button
      onClick={() => navigator.share ? handleShare('native') : setShowMenu(!showMenu)}
      className={`flex items-center gap-2 px-4 py-2 rounded-full glass hover:bg-primary/20 transition-colors ${className}`}
      style={{ fontFamily: "'Space Mono', monospace" }}
      data-testid="share-button-full"
    >
      <Share2 className="w-4 h-4" />
      <span className="text-sm">Share</span>
    </button>
  );
};

/**
 * Booking Success Share Card - Shareable confirmation
 */
export const BookingShareCard = ({ booking, event, className = '' }) => {
  if (!booking || !event) return null;
  
  const eventDate = event.start_at 
    ? new Date(event.start_at).toLocaleDateString('en-GB', { 
        weekday: 'short', 
        month: 'short', 
        day: 'numeric' 
      })
    : '';
  
  return (
    <div 
      className={`relative overflow-hidden rounded-2xl ${className}`}
      style={{ 
        background: 'linear-gradient(135deg, #0A0A0A 0%, #1A1A1A 50%, #0A0A0A 100%)',
        border: '1px solid rgba(204, 255, 0, 0.3)'
      }}
      data-testid="booking-share-card"
    >
      {/* Neon glow effect */}
      <div 
        className="absolute top-0 left-0 w-full h-1"
        style={{ background: 'linear-gradient(90deg, #CCFF00, #7000FF, #CCFF00)' }}
      />
      
      <div className="p-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-primary flex items-center justify-center">
              <span className="text-black font-bold text-xs">XT</span>
            </div>
            <span className="text-xs text-muted-foreground" style={{ fontFamily: "'Space Mono', monospace" }}>
              XTRIP CONFIRMED
            </span>
          </div>
          <ShareButton event={event} bundle={booking} variant="icon" />
        </div>
        
        {/* Event Info */}
        <h3 
          className="text-xl font-bold text-foreground mb-2"
          style={{ fontFamily: "'Space Mono', monospace" }}
        >
          {event.name}
        </h3>
        
        <div className="flex items-center gap-4 text-sm text-muted-foreground mb-4" style={{ fontFamily: "'Space Mono', monospace" }}>
          <span>📍 {event.venue_city}</span>
          <span>📅 {eventDate}</span>
        </div>
        
        {/* Booking Details */}
        <div 
          className="flex items-center gap-4 p-3 rounded-lg"
          style={{ background: 'rgba(204, 255, 0, 0.1)' }}
        >
          <span className="text-primary text-lg">✈️🏨🎫</span>
          <span className="text-sm text-foreground" style={{ fontFamily: "'Space Mono', monospace" }}>
            Flight + Hotel + Ticket
          </span>
        </div>
        
        {/* CTA */}
        <div className="mt-4 text-center">
          <p className="text-xs text-muted-foreground" style={{ fontFamily: "'Space Mono', monospace" }}>
            Plan your next rave at <span className="text-primary">xtrip.com</span>
          </p>
        </div>
      </div>
    </div>
  );
};

/**
 * Viral Referral Banner - Encourage sharing (NOW FUNCTIONAL)
 */
export const ViralReferralBanner = ({ event, className = '' }) => {
  const [showShare, setShowShare] = React.useState(false);
  
  const shareText = event 
    ? `Check out ${event.name} in ${event.venue_city} 🔊\n\nBook flights + hotel + ticket in one checkout at xtrip.com\n\n${getRandomHashtags(3)}`
    : `Discover underground techno & house events 🔊\n\nBook your next rave at xtrip.com\n\n${getRandomHashtags(3)}`;
  
  const shareUrl = typeof window !== 'undefined' ? window.location.href : 'https://xtrip.com';
  
  const handleShare = async (platform) => {
    if (platform === 'native' && navigator.share) {
      try {
        await navigator.share({
          title: event?.name || 'XTrip - Travel for the Night',
          text: shareText,
          url: shareUrl
        });
      } catch (err) {
        // User cancelled
      }
    } else if (platform === 'twitter') {
      window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(shareUrl)}`, '_blank', 'width=600,height=400');
    } else if (platform === 'whatsapp') {
      window.open(`https://wa.me/?text=${encodeURIComponent(shareText + ' ' + shareUrl)}`, '_blank');
    } else if (platform === 'telegram') {
      window.open(`https://t.me/share/url?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(shareText)}`, '_blank');
    } else if (platform === 'copy') {
      try {
        await navigator.clipboard.writeText(shareText + '\n\n' + shareUrl);
        toast.success('Copied to clipboard!');
      } catch (err) {
        toast.error('Failed to copy');
      }
    }
    setShowShare(false);
  };
  
  return (
    <div 
      className={`p-4 rounded-xl cursor-pointer hover:opacity-90 transition-opacity ${className}`}
      style={{ 
        background: 'linear-gradient(135deg, rgba(112, 0, 255, 0.2), rgba(204, 255, 0, 0.1))',
        border: '1px solid rgba(112, 0, 255, 0.3)'
      }}
      data-testid="viral-referral-banner"
      onClick={() => navigator.share ? handleShare('native') : setShowShare(!showShare)}
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="text-2xl">🔊</div>
          <div>
            <p 
              className="text-sm font-bold text-foreground"
              style={{ fontFamily: "'Space Mono', monospace" }}
            >
              Share the vibe
            </p>
            <p 
              className="text-xs text-muted-foreground"
              style={{ fontFamily: "'Space Mono', monospace" }}
            >
              Tag us @XTripHQ • {getRandomHashtags(2)}
            </p>
          </div>
        </div>
        <Share2 className="w-5 h-5 text-muted-foreground" />
      </div>
      
      {/* Share dropdown */}
      {showShare && (
        <div className="mt-3 pt-3 border-t border-foreground/10 flex flex-wrap gap-2">
          <button
            onClick={(e) => { e.stopPropagation(); handleShare('twitter'); }}
            className="flex items-center gap-2 px-3 py-2 rounded-lg bg-foreground/10 hover:bg-foreground/20 text-sm"
            style={{ fontFamily: "'Space Mono', monospace" }}
          >
            <Twitter className="w-4 h-4 text-[#1DA1F2]" /> Twitter
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); handleShare('whatsapp'); }}
            className="flex items-center gap-2 px-3 py-2 rounded-lg bg-foreground/10 hover:bg-foreground/20 text-sm"
            style={{ fontFamily: "'Space Mono', monospace" }}
          >
            <MessageCircle className="w-4 h-4 text-[#25D366]" /> WhatsApp
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); handleShare('telegram'); }}
            className="flex items-center gap-2 px-3 py-2 rounded-lg bg-foreground/10 hover:bg-foreground/20 text-sm"
            style={{ fontFamily: "'Space Mono', monospace" }}
          >
            <Send className="w-4 h-4 text-[#0088cc]" /> Telegram
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); handleShare('copy'); }}
            className="flex items-center gap-2 px-3 py-2 rounded-lg bg-foreground/10 hover:bg-foreground/20 text-sm"
            style={{ fontFamily: "'Space Mono', monospace" }}
          >
            <Copy className="w-4 h-4" /> Copy
          </button>
        </div>
      )}
    </div>
  );
};

export default ShareButton;
